import {useMutation, useMutationState} from '@tanstack/react-query';
import {useActiveStructure} from '@features/frame/frameHook';
import {toast} from 'sonner';
import {captureException} from '@sentry/react';
import {useAiThinkingForChat} from '@features/ai/useAiThinking';
import {atom, useAtomValue, useSetAtom} from 'jotai';
import {isEditingAtom} from '@features/chat/chatSelector';
import {useActiveScreenId} from '@features/activeScreenIdContext/useActiveScreenId';
import {useSubscription} from '@features/subscription/subscriptionHook';
import {usePaywallModal} from '@features/subscription/PaywallModal';
import amplitudeService from '@features/analytics/amplitudeService';
import {useAuthenticatedSession} from '@features/utils/useAuthenticatedSession';
import {useCallback, useContext, useEffect} from 'react';
import {FlowContext} from '@features/contexts/FlowContext';
import {trpc} from '@features/utils/trpc';
import {useExportToFigmaMulti} from './useExportToFigmaMulti';
import {getExportPrompt} from '@features/flow/utils/exportPromptTemplate';
import type {ExternalAgent} from '@/app/flow/components/useExternalAgents';

export const useExportToFigma = () => {
  const {flow} = useContext(FlowContext);
  const flowId = flow.id;
  const chatId = useActiveScreenId();
  const userId = useAuthenticatedSession().user.id;
  const {effectiveSubscription} = useSubscription();
  const {openPaywallModal} = usePaywallModal();
  const {exportToFigma, isPending} = useExportToFigmaMulti(flowId);

  const handleExportClick = () => {
    amplitudeService.logEvent('[Flow] Copy to Figma click', {
      userId,
      flowId,
      chatId,
    });

    if (!effectiveSubscription?.isActive || effectiveSubscription.isPastDue) {
      openPaywallModal('export-to-figma');
      return;
    }

    if (!chatId) {
      return;
    }

    toast.promise(exportToFigma([chatId]), {
      loading: 'Exporting to Figma...',
      success: 'Copied, press "Cmd+V" in Figma',
      error: 'Error exporting to Figma, try again',
    });
  };

  return {
    handleExportClick,
    isPending,
  };
};

export const useExportEnabled = () => {
  const {flow} = useContext(FlowContext);
  return (
    flow.genType === 'web-html-css' ||
    flow.genType === 'mobile-html-css' ||
    flow.genType === 'components'
  );
};

// Cached output of the disabled-computation. Subscribers (canvas dropdowns)
// only re-render when the boolean flips, instead of re-running on each of the
// 7 underlying signal changes.
const exportButtonDisabledAtom = atom(true);

const useComputeExportButtonDisabled = () => {
  const chatId = useActiveScreenId();
  const {activeStructure} = useActiveStructure(chatId);
  const isAiThinking = useAiThinkingForChat(chatId);
  const isEditing = useAtomValue(isEditingAtom);
  const {effectiveSubscription} = useSubscription();
  const trpcUtil = trpc.useUtils();

  const isFigmaExportPending = trpcUtil.figma.exportToFigma.isMutating() > 0;

  const isHtmlCssExportPending =
    useMutationState({
      filters: {mutationKey: ['exportToHtmlCss'], status: 'pending'},
    }).length > 0;

  return (
    !activeStructure?.htmlCss ||
    isFigmaExportPending ||
    isHtmlCssExportPending ||
    !effectiveSubscription ||
    isAiThinking ||
    isEditing
  );
};

// Mount once inside the canvas tree. Centralizes the 7 subscriptions so they
// run on a single fiber instead of inside every per-screen toolbar dropdown.
export const ExportButtonDisabledBridge = () => {
  const value = useComputeExportButtonDisabled();
  const setValue = useSetAtom(exportButtonDisabledAtom);
  useEffect(() => {
    setValue(value);
  }, [value, setValue]);
  // Reset to disabled on unmount so the next flow doesn't inherit stale state
  // before its own bridge effect runs.
  useEffect(() => () => setValue(true), [setValue]);
  return null;
};

export const useExportButtonDisabled = () =>
  useAtomValue(exportButtonDisabledAtom);

export const useExportToHtmlCss = () => {
  const userId = useAuthenticatedSession().user.id;
  const chatId = useActiveScreenId();
  const {effectiveSubscription} = useSubscription();
  const {openPaywallModal} = usePaywallModal();
  const {flow} = useContext(FlowContext);
  const flowId = flow.id;
  const utils = trpc.useUtils();
  const getExportHtml = trpc.screens.getExportHtml.useMutation();
  const incrementExportCount =
    trpc.subscription.incrementFigmaExportCount.useMutation({
      onSuccess: () => {
        void utils.subscription.getSubscriptionInfo.invalidate();
      },
    });

  const exportMutation = useMutation({
    mutationKey: ['exportToHtmlCss'],
    mutationFn: async () => {
      if (!chatId) return;

      const clipboardItem = new ClipboardItem({
        'text/plain': getExportHtml.mutateAsync({screenId: chatId}),
      });
      await navigator.clipboard.write([clipboardItem]);
      // Increment export count for free users (awaited to keep button disabled until complete)
      await incrementExportCount.mutateAsync();

      amplitudeService.logEvent('[Flow] Copy to HTML/CSS success', {
        flowId,
        userId,
        chatId,
      });
    },
    onError: (error) => {
      console.error('Error copying to HTML/CSS:', error);
      captureException(error);
      amplitudeService.logEvent('[Flow] Copy to HTML/CSS error', {
        userId,
        flowId,
        chatId,
        ...(error instanceof Error ? {error: error.message} : {}),
      });
    },
  });

  return useCallback(() => {
    amplitudeService.logEvent('[Flow] Copy to HTML/CSS click', {
      flowId,
      userId,
      chatId,
    });
    if (!effectiveSubscription?.canExport) {
      openPaywallModal('export-to-html-css');
      return;
    }
    toast.promise(exportMutation.mutateAsync(), {
      loading: 'Exporting to HTML/CSS...',
      success: 'Copied, press "Cmd+V" in your editor',
      error: 'Error exporting to HTML/CSS, try again.',
    });
  }, [
    chatId,
    effectiveSubscription?.canExport,
    exportMutation,
    flowId,
    openPaywallModal,
    userId,
  ]);
};

export const useExportToHtmlCssWithAgentPrompt = () => {
  const userId = useAuthenticatedSession().user.id;
  const chatId = useActiveScreenId();
  const {effectiveSubscription} = useSubscription();
  const {openPaywallModal} = usePaywallModal();
  const {flow} = useContext(FlowContext);
  const flowId = flow.id;
  const utils = trpc.useUtils();
  const getExportHtml = trpc.screens.getExportHtml.useMutation();
  const incrementExportCount =
    trpc.subscription.incrementFigmaExportCount.useMutation({
      onSuccess: () => {
        void utils.subscription.getSubscriptionInfo.invalidate();
      },
    });

  return useCallback(
    (externalAgent: ExternalAgent) => {
      const ampProps = {flowId, userId, chatId, agent: externalAgent.id};
      amplitudeService.logEvent(
        '[Flow] Export to external agent click',
        ampProps,
      );

      if (!effectiveSubscription?.canExport) {
        openPaywallModal('export-to-external-agent');
        return;
      }

      if (!chatId) return;

      const promise = (async () => {
        try {
          const clipboardItem = new ClipboardItem({
            'text/plain': (async () => {
              const html = await getExportHtml.mutateAsync({screenId: chatId});
              const promptWithHtml = getExportPrompt(html);

              return new Blob([promptWithHtml], {type: 'text/plain'});
            })(),
          });
          await navigator.clipboard.write([clipboardItem]);
          // Increment export count for free users (awaited to keep button disabled until complete)
          await incrementExportCount.mutateAsync();

          amplitudeService.logEvent(
            '[Flow] Export to external agent success',
            ampProps,
          );
        } catch (error) {
          console.error('Error copying design with prompt:', error);
          captureException(error);
          throw error;
        }
      })();

      toast.promise(promise, {
        loading: 'Copying design with prompt...',
        success: `Copied! Paste into ${externalAgent.name}`,
        error: 'Error copying design, try again.',
      });
    },
    [
      chatId,
      effectiveSubscription?.canExport,
      flowId,
      getExportHtml,
      incrementExportCount,
      openPaywallModal,
      userId,
    ],
  );
};
